import { useCallback, useEffect, useRef, useState } from "react";
import { api, streamWorkspace } from "@/lib/api";

const fromStored = (m) => ({
  id: m.id,
  role: m.role,
  content: m.content || "",
  artifacts: m.artifacts || [],
  tools: (m.tool_trace || []).map((t) => ({ call_id: t.call_id, name: t.name, label: t.label || t.name, ok: t.ok, done: true, artifact_ids: t.artifact_ids || [] })),
  created_at: m.created_at,
  streaming: false,
});

/**
 * Conversation state for the AI Workspace (page + side panel). Handles creation, loading,
 * sending and the SSE event stream (meta/delta/tool_start/tool_result/artifact/done/error).
 *
 * Robustness rules:
 *  - `initialConversationId` only triggers a load when it differs from the conversation we already hold
 *    (the page writes the new id back to the URL right after creation – that must not restart/abort the stream).
 *  - The stream closing (for whatever reason) always clears `sending`, so the composer can never get stuck.
 */
export const useWorkspaceChat = ({ initialConversationId = null } = {}) => {
  const [conversationId, setConversationId] = useState(initialConversationId);
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);
  const convIdRef = useRef(initialConversationId);
  const sendingRef = useRef(false);

  const setConvId = (id) => {
    convIdRef.current = id;
    setConversationId(id);
  };
  const setSendingState = (v) => {
    sendingRef.current = v;
    setSending(v);
  };

  const cancelStream = () => {
    abortRef.current?.();
    abortRef.current = null;
  };

  const reset = useCallback(() => {
    cancelStream();
    setConvId(null);
    setConversation(null);
    setMessages([]);
    setSendingState(false);
    setError(null);
  }, []);

  const load = useCallback(async (id) => {
    if (!id) return;
    cancelStream();
    setSendingState(false);
    setConvId(id);
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/workspace/conversations/${id}`);
      // Ignore stale responses if the user moved on meanwhile.
      if (convIdRef.current !== id) return;
      setConversation(data.conversation);
      setMessages((data.messages || []).map(fromStored));
    } catch (e) {
      setError(e?.response?.data?.detail || "Kunne ikke hente samtalen.");
    } finally {
      if (convIdRef.current === id) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (initialConversationId && initialConversationId !== convIdRef.current) load(initialConversationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialConversationId]);

  const patchAssistant = (id, fn) => setMessages((prev) => prev.map((m) => (m.id === id ? fn(m) : m)));

  const send = useCallback(
    async (text, { plantId = null } = {}) => {
      const content = (text || "").trim();
      if (!content || sendingRef.current) return null;
      setSendingState(true);
      setError(null);
      let convId = convIdRef.current;
      try {
        if (!convId) {
          const { data } = await api.post("/workspace/conversations", { plant_id: plantId });
          convId = data.id;
          setConvId(convId);
          setConversation(data);
        }
      } catch (e) {
        setSendingState(false);
        setError(e?.response?.data?.detail || "Kunne ikke starte samtalen.");
        return null;
      }
      const tempUser = `u-${Date.now()}`;
      const tempAssistant = `a-${Date.now()}`;
      setMessages((prev) => [
        ...prev,
        { id: tempUser, role: "user", content, artifacts: [], tools: [], created_at: new Date().toISOString(), plant_id: plantId },
        { id: tempAssistant, role: "assistant", content: "", artifacts: [], tools: [], created_at: new Date().toISOString(), streaming: true },
      ]);
      let currentId = tempAssistant;
      let finished = false;
      const finish = () => {
        if (finished) return;
        finished = true;
        patchAssistant(currentId, (m) => ({ ...m, streaming: false }));
        setSendingState(false);
        abortRef.current = null;
      };
      abortRef.current = streamWorkspace(
        convId,
        { content, plant_id: plantId },
        (ev) => {
          if (ev.type === "meta") {
            setMessages((prev) => prev.map((m) => (m.id === tempUser ? { ...m, id: ev.user_message_id || m.id } : m.id === currentId ? { ...m, id: ev.message_id, model: ev.model, context_plant: ev.context_plant } : m)));
            currentId = ev.message_id;
            if (ev.title) setConversation((c) => ({ ...(c || {}), id: convId, title: ev.title }));
          } else if (ev.type === "delta") {
            patchAssistant(currentId, (m) => ({ ...m, content: m.content + ev.text }));
          } else if (ev.type === "tool_start") {
            patchAssistant(currentId, (m) => {
              const exists = m.tools.find((t) => t.call_id === ev.call_id);
              const tools = exists ? m.tools.map((t) => (t.call_id === ev.call_id ? { ...t, label: ev.label || t.label } : t)) : [...m.tools, { call_id: ev.call_id, name: ev.name, label: ev.label, done: false }];
              return { ...m, tools };
            });
          } else if (ev.type === "tool_result") {
            patchAssistant(currentId, (m) => ({ ...m, tools: m.tools.map((t) => (t.call_id === ev.call_id ? { ...t, done: true, ok: ev.ok, label: ev.label || t.label, artifact_count: ev.artifact_count } : t)) }));
          } else if (ev.type === "artifact") {
            patchAssistant(currentId, (m) => ({ ...m, artifacts: [...m.artifacts, ev.artifact] }));
          } else if (ev.type === "error") {
            patchAssistant(currentId, (m) => ({ ...m, error: ev.message }));
            finish();
          } else if (ev.type === "done") {
            finish();
          }
        },
        (err) => {
          patchAssistant(currentId, (m) => ({ ...m, error: err.message || "Noe gikk galt." }));
          finish();
        },
        ({ aborted }) => {
          // Stream closed without a done/error event (network hiccup, server closed) – never leave the UI stuck.
          if (!finished) {
            if (!aborted) patchAssistant(currentId, (m) => (m.content ? m : { ...m, error: "Svaret ble avbrutt før det var ferdig. Prøv igjen." }));
            finish();
          }
        },
      );
      return convId;
    },
    [],
  );

  const stop = useCallback(() => {
    cancelStream();
    setMessages((prev) => prev.map((m) => (m.streaming ? { ...m, streaming: false } : m)));
    setSendingState(false);
  }, []);

  useEffect(() => () => abortRef.current?.(), []);

  return { conversationId, conversation, messages, sending, loading, error, send, load, reset, stop, setConversation };
};
